<template>
  <div class="group-dialog" id="groupDialog" tabindex="-1" @keydown.escape="close">
    <div class="group-dialog-head">
      <div class="group-dialog-title" id="groupDialogTitle">{{ dialogTitle }}</div>
      <button class="group-dialog-close" id="groupDialogClose" type="button" aria-label="关闭" @click="close">×</button>
    </div>
    <div class="group-dialog-body" id="groupDialogBody">

      <!-- Create group mode -->
      <template v-if="mode === 'create-group'">
        <div class="group-dialog-note">选择至少 2 位成员并填写群名称，创建后会生成一个新的群组会话。</div>
        <input class="group-dialog-rename" type="text" v-model="groupName" placeholder="输入群名称" maxlength="30" />
        <input class="group-dialog-search" type="text" v-model="groupCreateQuery" placeholder="搜索要拉群的人" />
        <div v-if="selectedMembers.length" class="group-dialog-selection">
          <span v-for="id in selectedMembers" :key="id" class="group-dialog-selection-chip">
            {{ getMemberName(id) }}
            <button class="group-dialog-selection-remove" type="button" @click="toggleMember(id)">
              <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
            </button>
          </span>
        </div>
        <div v-else class="group-dialog-note">还没有选择成员。</div>
        <div class="group-dialog-member-list">
          <template v-if="filteredCreateMembers.length">
            <div
              v-for="member in filteredCreateMembers"
              :key="member.id"
              class="group-dialog-member"
            >
              <div class="group-dialog-member-main">
                <div class="group-dialog-member-avatar" :style="{ background: member.avatarUrl ? 'transparent' : (member.avatarBackground || '') }"><img v-if="member.avatarUrl" :src="member.avatarUrl" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ member.avatarText }}</template></div>
                <div class="group-dialog-member-copy">
                  <div class="group-dialog-member-name">{{ member.name }}</div>
                  <div class="group-dialog-member-sub">{{ member.sub }}</div>
                </div>
              </div>
              <button
                class="group-dialog-inline-btn"
                :class="{ active: selectedMembers.includes(member.id) }"
                type="button"
                @click="toggleMember(member.id)"
              >{{ selectedMembers.includes(member.id) ? '已选择' : '选择' }}</button>
            </div>
          </template>
          <div v-else class="group-dialog-empty">暂无可选成员。</div>
        </div>
      </template>

      <!-- Create direct chat mode -->
      <template v-else-if="mode === 'create-direct'">
        <div class="group-dialog-note">选择一位联系人发起私聊。</div>
        <input class="group-dialog-search" type="text" v-model="directQuery" placeholder="搜索联系人" />
        <div v-if="selectedDirect" class="group-dialog-selection">
          <span class="group-dialog-selection-chip">
            {{ getDirectName() }}
            <button class="group-dialog-selection-remove" type="button" @click="selectedDirect = null">
              <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
            </button>
          </span>
        </div>
        <div v-else class="group-dialog-note">还没有选择联系人。</div>
        <div class="group-dialog-member-list">
          <template v-if="filteredDirectContacts.length">
            <div
              v-for="member in filteredDirectContacts"
              :key="member.id"
              class="group-dialog-member"
            >
              <div class="group-dialog-member-main">
                <div class="group-dialog-member-avatar" :style="{ background: member.avatarUrl ? 'transparent' : (member.avatarBackground || '') }"><img v-if="member.avatarUrl" :src="member.avatarUrl" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ member.avatarText }}</template></div>
                <div class="group-dialog-member-copy">
                  <div class="group-dialog-member-name">{{ member.name }}</div>
                  <div class="group-dialog-member-sub">{{ member.sub }}</div>
                </div>
              </div>
              <button
                class="group-dialog-inline-btn"
                :class="{ active: selectedDirect === member.id }"
                type="button"
                @click="selectedDirect = selectedDirect === member.id ? null : member.id"
              >{{ selectedDirect === member.id ? '已选择' : '选择' }}</button>
            </div>
          </template>
          <div v-else class="group-dialog-empty">暂无联系人。</div>
        </div>
      </template>

      <!-- Rename mode -->
      <template v-else-if="mode === 'rename'">
        <div class="group-dialog-note">修改后会同步到群聊列表、主会话标题和话题页标题。</div>
        <input ref="primaryInput" class="group-dialog-rename" type="text" v-model="renameDraft" placeholder="输入新名称" maxlength="30" @keydown.enter.prevent="onEnterConfirm" />
      </template>

      <!-- Topic / Agent-topic mode -->
      <template v-else-if="mode === 'topic' || mode === 'agent-topic'">
        <div class="group-dialog-note">{{ mode === 'agent-topic' ? '为当前 Agent 新开一个任务会话，与现有任务并行进行、互不打扰；你可以随时在左侧任务列表切换、查看进度或继续对话。' : '创建后会把新话题加入当前会话的话题列表，并切换到该话题视图。' }}</div>
        <input ref="primaryInput" class="group-dialog-rename" type="text" v-model="topicDraft" :placeholder="mode === 'agent-topic' ? '输入新任务名称' : '输入新话题名称'" maxlength="30" @keydown.enter.prevent="onEnterConfirm" />
      </template>

      <!-- Exit topic mode -->
      <template v-else-if="mode === 'exit-topic'">
        <div class="group-dialog-note">确认关闭当前话题后，该话题会从右侧话题栏中移除，并切换到剩余话题。</div>
        <div v-if="closeTopicError" class="group-dialog-note" style="color:#dc2626;background:rgba(239,68,68,.06);border-color:rgba(239,68,68,.12);">{{ closeTopicError }}</div>
      </template>

      <!-- Exit mode -->
      <template v-else-if="mode === 'exit'">
        <div class="group-dialog-note">确认退出后，该群聊会从左侧列表和头像快捷栏中移除。</div>
        <div v-if="closeTopicError" class="group-dialog-note" style="color:#dc2626;background:rgba(239,68,68,.06);border-color:rgba(239,68,68,.12);">{{ closeTopicError }}</div>
      </template>

      <!-- Invite mode -->
      <template v-else-if="mode === 'invite'">
        <input class="group-dialog-search" type="text" v-model="inviteQuery" placeholder="搜索成员" />
        <div class="group-dialog-note">从候选列表中邀请新成员加入当前群聊。</div>
        <div class="group-dialog-member-list">
          <template v-if="filteredInviteMembers.length">
            <div
              v-for="member in filteredInviteMembers"
              :key="member.id || member.name"
              class="group-dialog-member"
            >
              <div class="group-dialog-member-main">
                <div class="group-dialog-member-avatar" :style="{ background: member.avatarUrl ? 'transparent' : (member.avatarBackground || '') }"><img v-if="member.avatarUrl" :src="member.avatarUrl" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ member.avatarText }}</template></div>
                <div class="group-dialog-member-copy">
                  <div class="group-dialog-member-name">{{ member.name }}</div>
                  <div class="group-dialog-member-sub">{{ member.sub }}</div>
                </div>
              </div>
              <button
                class="group-dialog-inline-btn"
                :class="{ active: invitedIds.includes(member.id), loading: invitingIds.includes(member.id) }"
                type="button"
                :disabled="invitedIds.includes(member.id) || invitingIds.includes(member.id)"
                @click="inviteMember(member)"
              >{{ invitingIds.includes(member.id) ? '邀请中…' : invitedIds.includes(member.id) ? '邀请成功' : '邀请' }}</button>
            </div>
          </template>
          <div v-else class="group-dialog-empty">暂无可邀请成员了。</div>
        </div>
      </template>

    </div>
    <div class="group-dialog-footer" id="groupDialogFooter">
      <template v-if="mode === 'exit' || mode === 'exit-topic'">
        <button class="group-dialog-btn" type="button" @click="close">取消</button>
        <button class="group-dialog-btn danger" type="button" @click="confirm">{{ mode === 'exit-topic' ? '关闭话题' : '退出群聊' }}</button>
      </template>
      <template v-else-if="mode === 'invite'">
        <button class="group-dialog-btn primary" type="button" @click="close">完成</button>
      </template>
      <template v-else>
        <button class="group-dialog-btn" type="button" @click="close">取消</button>
        <button class="group-dialog-btn primary" type="button" @click="confirm" :disabled="confirmDisabled">{{ confirmLabel }}</button>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import { api } from '@/utils/api'
import { solidFromColor } from '@/utils/avatarColor'

export default {
  name: 'GroupDialog',
  data() {
    return {
      groupName: '',
      groupCreateQuery: '',
      directQuery: '',
      selectedDirect: null,
      renameDraft: '',
      topicDraft: '',
      inviteQuery: '',
      selectedMembers: [],
      invitedIds: [],
      invitingIds: [],
      inviteAgents: [],   // invite 模式下额外拉取的 agent 列表
      createAgents: [],   // create-group 模式下的 agent 列表
      inviteListSnapshot: [],  // 打开弹窗时的成员快照，防止邀请后列表刷新导致消失
      closeTopicError: ''
    }
  },
  watch: {
    'groupDialog.open': {
      immediate: true,
      handler(open) {
        if (open) this.$nextTick(() => { if (this.$el) this.$el.focus() })
        // 创建话题/任务、改名：弹窗一开就把光标落到输入框（rename 选中已有文字方便改）
        if (open && ['topic', 'agent-topic', 'rename'].includes(this.groupDialog.mode)) {
          this.$nextTick(() => {
            const el = this.$refs.primaryInput
            if (el) { el.focus(); if (this.groupDialog.mode === 'rename' && el.select) el.select() }
          })
        }
        if (open && this.groupDialog.mode === 'create-group') {
          this.selectedMembers = []
          this.groupCreateQuery = ''
          this.groupName = ''
          this.createAgents = []
          this.fetchTeamMembers()
          this.$store.dispatch('fetchAgentsForInvite').then(agents => {
            this.createAgents = agents
          })
        } else if (open && this.groupDialog.mode === 'create-direct') {
          this.selectedDirect = null
          this.directQuery = ''
          this.fetchDirectContacts()
        } else if (open && this.groupDialog.mode === 'invite') {
          this.invitedIds = []
          this.invitingIds = []
          this.inviteQuery = ''
          this.inviteAgents = []
          this.inviteListSnapshot = []
          const convId = this.currentConversationId
          if (convId) {
            // 并行拉：可邀请用户 + 可添加的 agent 列表（已过滤群内已有的）
            Promise.all([
              this.fetchAvailableMembers(convId),
              this.$store.dispatch('fetchAgentsForInvite', convId).then(agents => {
                this.inviteAgents = agents
              })
            ]).then(() => {
              // 数据加载完成后快照，后续邀请不会导致列表变化
              this.inviteListSnapshot = [...this.apiInviteMembers]
            })
          }
        }
      }
    }
  },
  computed: {
    ...mapState(['groupDialog', 'availableMembers', 'teamMembers', 'currentTopicKey', 'sessionTopicPages']),
    ...mapGetters(['currentConversationId']),
    mode() { return this.groupDialog.mode },
    sessionKey() { return this.groupDialog.sessionKey },
    dialogTitle() {
      const titles = {
        'create-group': '发起群聊',
        'create-direct': '发起会话',
        rename: '修改群名称',
        topic: '创建新话题',
        'agent-topic': '新建任务',
        'exit-topic': '关闭话题',
        exit: '退出群聊',
        invite: '邀请成员'
      }
      return titles[this.mode] || '群聊管理'
    },
    apiInviteMembers() {
      const convId = this.currentConversationId
      // Agent 列表（排前面）
      const agentList = this.inviteAgents.map((a, i) => {
        const name = a.name || '未命名Agent'
        return {
          id: a.id || a.userId,
          type: 'agent',
          name,
          avatarUrl: a.avatarUrl || null,
          avatarText: String(name).slice(0, 1),
          avatarBackground: solidFromColor(a.avatarColorRgb),
          sub: 'Agent' + (a.roleName ? ' · ' + a.roleName : '')
        }
      })
      // 可邀请用户列表（排后面）
      const raw = convId ? (this.availableMembers[convId] || []) : []
      const userList = raw.map((mb, i) => {
        const name = mb.name || mb.displayName || mb.display_name || '成员'
        return {
          id: mb.id || mb.userId || mb.user_id,
          type: mb.type || 'account',
          name,
          avatarUrl: mb.avatarUrl || null,
          avatarText: String(name).slice(0, 1),
          avatarBackground: solidFromColor(mb.avatarColorRgb),
          sub: mb.type === 'agent' ? 'Agent' : ('成员' + (mb.positionName || mb.position_name || mb.position || mb.role || mb.title ? ' · ' + (mb.positionName || mb.position_name || mb.position || mb.role || mb.title) : ''))
        }
      })
      // 去重：已在 agentList 里的 id 不再出现在 userList
      const agentIds = new Set(agentList.map(a => a.id))
      return [...agentList, ...userList.filter(u => !agentIds.has(u.id))]
    },
    createMembersList() {
      // Agent 列表在前
      const agentList = (this.createAgents || []).map((a, i) => ({
        id: a.id,
        name: a.name || '未命名Agent',
        type: 'agent',
        avatarUrl: a.avatarUrl || null,
        avatarText: String(a.name || 'A').slice(0, 1),
        avatarBackground: solidFromColor(a.avatarColorRgb),
        sub: 'Agent' + (a.roleName ? ' · ' + a.roleName : '')
      }))
      // 团队成员在后（排除自己）
      const userList = (this.teamMembers || []).filter(mb => !mb.isSelf).map((mb, i) => ({
        id: mb.userId || mb.id,
        name: mb.name || '成员',
        type: mb.type || 'account',
        avatarUrl: mb.avatarUrl || null,
        avatarText: String(mb.name || '成员').slice(0, 1),
        avatarBackground: solidFromColor(mb.avatarColorRgb),
        sub: mb.type === 'agent' ? 'Agent' : ('成员' + (mb.position ? ' · ' + mb.position : ''))
      }))
      // 去重
      const agentIds = new Set(agentList.map(a => a.id))
      return [...agentList, ...userList.filter(u => !agentIds.has(u.id))]
    },
    filteredCreateMembers() {
      const q = this.groupCreateQuery.trim().toLowerCase()
      const list = this.createMembersList
      return q ? list.filter(m => m.name.toLowerCase().includes(q)) : list
    },
    filteredDirectContacts() {
      const q = this.directQuery.trim().toLowerCase()
      const list = this.directContactsList
      return q ? list.filter(m => m.name.toLowerCase().includes(q)) : list
    },
    directContactsList() {
      const myId = this.$store.state.currentUserId
      return (this.$store.state.directContacts || []).filter(mb => {
        const id = mb.userId || mb.id
        return id !== myId && !mb.isSelf
      }).map((mb, i) => {
        const name = mb.name || '成员'
        return {
          id: mb.userId || mb.id,
          name,
          avatarUrl: mb.avatarUrl || null,
          avatarText: String(name).slice(0, 1),
          avatarBackground: solidFromColor(mb.avatarColorRgb),
          sub: '成员' + (mb.positionName || mb.position_name || mb.position ? ' · ' + (mb.positionName || mb.position_name || mb.position) : '')
        }
      })
    },
    filteredInviteMembers() {
      const q = this.inviteQuery.trim().toLowerCase()
      const list = this.inviteListSnapshot.length ? this.inviteListSnapshot : this.apiInviteMembers
      return q ? list.filter(m => m.name.toLowerCase().includes(q)) : list
    },
    isDefaultTopic() {
      const convId = this.currentConversationId
      const topicId = this.currentTopicKey
      if (!convId || !topicId) return false
      const session = this.$store.state.sessionItems.find(s => s.conversationId === convId)
      if (!session || !session.topicKey) return false
      const page = this.sessionTopicPages[session.topicKey]
      if (!page) return false
      const topic = page.topics.find(t => t.key === topicId)
      return !!(topic && topic.isDefault)
    },
    confirmLabel() {
      const labels = {
        'create-group': '创建群聊',
        'create-direct': '发起会话',
        rename: '保存',
        topic: '创建话题',
        'agent-topic': '创建任务'
      }
      return labels[this.mode] || '确认'
    },
    confirmDisabled() {
      if (this.mode === 'create-direct') {
        return !this.selectedDirect
      }
      if (this.mode === 'create-group') {
        return !this.groupName.trim() || this.selectedMembers.length < 2
      }
      if (this.mode === 'topic' || this.mode === 'agent-topic') {
        return !this.topicDraft.trim()
      }
      return false
    }
  },
  methods: {
    ...mapActions(['createGroupSession', 'createDirectSession', 'exitGroupSession', 'createConversationTopic', 'fetchAvailableMembers', 'fetchTeamMembers', 'fetchDirectContacts', 'inviteMembers']),
    close() {
      this.$store.commit('SET_GROUP_DIALOG', { open: false })
    },
    async confirm() {
      if (this.mode === 'create-group') {
        // 建立 id → type 映射，区分 agent / 同事
        const typeMap = {}
        ;(this.createAgents || []).forEach(a => { if (a.id) typeMap[a.id] = 'agent' })
        ;(this.teamMembers || []).forEach(mb => {
          const id = mb.userId || mb.id
          if (id && !typeMap[id]) typeMap[id] = mb.type || 'account'
        })
        const participantIds = []
        const agentIds = []
        this.selectedMembers.forEach(id => {
          if (typeMap[id] === 'agent') {
            agentIds.push(id)
          } else {
            participantIds.push(id)
          }
        })
        console.log('[GroupDialog] typeMap:', typeMap, 'participantIds:', participantIds, 'agentIds:', agentIds)
        await this.createGroupSession({ name: this.groupName || '新群聊', participantIds, agentIds })
      } else if (this.mode === 'create-direct') {
        if (this.selectedDirect) {
          await this.createDirectSession({ participantId: this.selectedDirect })
        }
      } else if (this.mode === 'rename') {
        const newName = this.renameDraft.trim()
        if (!newName) return
        const convId = this.currentConversationId
        if (!convId) return
        const res = await api.updateConversation(convId, { name: newName })
        this.$store.commit('UPDATE_SESSION_ITEM', { key: this.sessionKey, updates: { name: res.name || newName } })
      } else if (this.mode === 'topic' || this.mode === 'agent-topic') {
        const title = this.topicDraft.trim()
        if (!title) return
        const convId = this.currentConversationId
        if (convId) {
          await this.createConversationTopic({ conversationId: convId, name: title })
        }
        this.topicDraft = ''
      } else if (this.mode === 'exit-topic') {
        const convId = this.currentConversationId
        const topicId = this.currentTopicKey
        if (convId && topicId) {
          this.closeTopicError = ''
          if (this.isDefaultTopic) return
          try {
            await api.deleteTopic(convId, topicId)
          } catch (e) {
            this.closeTopicError = e.message || '关闭话题失败'
            return
          }
          // 重新拉取会话列表刷新话题数据和样式
          await this.$store.dispatch('fetchConversations', { force: true })
          // 切换到剩余的第一个话题
          const s = this.$store.state.sessionItems.find(s => s.conversationId === convId)
          if (s && s.topicKey) {
            const page = this.$store.state.sessionTopicPages[s.topicKey]
            if (page && page.topics.length) {
              this.$store.dispatch('switchTopic', { topicKey: page.topics[0].key, pageKey: s.topicKey })
            }
          }
        }
      } else if (this.mode === 'exit') {
        try {
          await this.exitGroupSession(this.sessionKey)
        } catch (e) {
          this.closeTopicError = e.message || '退出群聊失败'
          return
        }
      }
      this.close()
    },
    // 输入框回车直接触发确认（创建话题/任务、改名）；IME 拼字中的回车不触发，禁用态不触发
    onEnterConfirm(e) {
      if (e && (e.isComposing || e.keyCode === 229)) return
      if (this.confirmDisabled) return
      this.confirm()
    },
    toggleMember(id) {
      const idx = this.selectedMembers.indexOf(id)
      if (idx >= 0) {
        this.selectedMembers.splice(idx, 1)
      } else {
        this.selectedMembers.push(id)
      }
    },
    getMemberName(id) {
      const m = this.createMembersList.find(mb => mb.id === id)
      return m ? m.name : id
    },
    getDirectName() {
      const m = this.directContactsList.find(mb => mb.id === this.selectedDirect)
      return m ? m.name : this.selectedDirect
    },
    async inviteMember(member) {
      if (!member || !member.id) return
      if (this.invitingIds.includes(member.id) || this.invitedIds.includes(member.id)) return
      const convId = this.currentConversationId
      if (!convId) return
      this.invitingIds.push(member.id)
      try {
        if (member.type === 'agent') {
          await this.$store.dispatch('addAgentsToConversation', { conversationId: convId, agentIds: [member.id] })
        } else {
          await this.inviteMembers({ conversationId: convId, userIds: [member.id] })
        }
        this.invitedIds.push(member.id)
        this.$store.commit('SET_ONLINE_MEMBERS_REFRESH', Date.now())
      } catch (e) {
        console.warn('[GroupDialog] invite failed:', e.message)
      } finally {
        const idx = this.invitingIds.indexOf(member.id)
        if (idx >= 0) this.invitingIds.splice(idx, 1)
      }
    }
  }
}
</script>
